/**
 * SD-8.2 (UX-DR14, §C.4): the live envelope preview, INSIDE the setbacks card.
 *
 * It sits in the card rather than a page-level rail so its scope is unambiguous: this picture
 * is about these three numbers and nothing else on the page. It renders `referenceRoof`'s fixed
 * generic face — never a customer's — and the envelope it draws comes from the same
 * `setbackEnvelope` → `offsetPolygonPerEdge` chain the canvas draws with. Nothing geometric is
 * computed in this file; a preview with its own math would eventually disagree with the canvas.
 *
 * COLOURS ARE THEME TOKENS, unlike the canvas palette. `canvasPalette.ts` is absolute hex
 * because its objects sit on photographic imagery; this preview sits on a card, so UX-DR21's
 * normal rule applies and every colour is an `hsl(var(--…))` token.
 *
 * THE ZERO-FIT BRANCH IS UNREACHABLE HERE, AND THAT IS RECORDED, NOT DISCOVERED: §C.4 pins that
 * even 48 in on every edge leaves 44 positions on this roof (test-pinned in referenceRoof).
 * The branch is kept because the count-to-warning contract belongs to the component, not to the
 * particular roof — and the honest label below the picture says why one reference roof cannot
 * sanity-check every real one.
 */
import { useMemo } from 'react';
import type { Pt } from '@/lib/solar/geometry/types';
import type { SetbackInches } from '@/lib/solar/setbackEnvelope';
import {
  REFERENCE_FACE_H_M,
  REFERENCE_FACE_W_M,
  REFERENCE_PLANE,
  referenceEnvelope,
  referencePreview,
} from './referenceRoof';

/** Padding around the face, metres, for the edge labels. */
const PAD_M = 1.6;

/** SVG y grows down; the geometry's y grows north. Flip once, here. */
const flipY = (y: number): number => REFERENCE_FACE_H_M - y;

const toPath = (pts: readonly Pt[]): string =>
  pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(3)} ${flipY(y).toFixed(3)}`).join(' ') +
  ' Z';

export function EnvelopePreview({ setbacks }: { setbacks: SetbackInches }) {
  const { ridge, eave, rake } = setbacks;
  const regions = useMemo(() => referenceEnvelope({ ridge, eave, rake }), [ridge, eave, rake]);
  const preview = useMemo(() => referencePreview({ ridge, eave, rake }), [ridge, eave, rake]);

  const deltaText =
    preview.delta === 0
      ? `matches the shipped default (${preview.shipped})`
      : preview.delta > 0
        ? `+${preview.delta} vs the shipped default (${preview.shipped})`
        : `${preview.delta} vs the shipped default (${preview.shipped})`;

  const labelStyle = { fontSize: 0.62, fill: 'hsl(var(--muted-foreground))' } as const;

  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
      <svg
        viewBox={`${-PAD_M} ${-PAD_M} ${REFERENCE_FACE_W_M + 2 * PAD_M} ${REFERENCE_FACE_H_M + 2 * PAD_M}`}
        className="w-full"
        role="img"
        aria-label={`Setback envelope on the reference roof: ${preview.count} panel positions`}
      >
        {/* The face. */}
        <path
          d={toPath(REFERENCE_PLANE.polygon_m as readonly Pt[])}
          fill="hsl(var(--muted))"
          stroke="hsl(var(--border))"
          strokeWidth={0.06}
        />
        {/* The placeable envelope — exactly the regions the packer may fill. */}
        {regions.map((region, i) => (
          <path
            key={i}
            d={toPath(region)}
            fill="hsl(var(--primary) / 0.15)"
            stroke="hsl(var(--primary))"
            strokeWidth={0.09}
            strokeDasharray="0.4 0.22"
          />
        ))}
        {/* Which slider owns which edge. The unit is rendered here too, never typed. */}
        <text x={REFERENCE_FACE_W_M / 2} y={-0.5} textAnchor="middle" style={labelStyle}>
          Ridge {ridge} in
        </text>
        <text
          x={REFERENCE_FACE_W_M / 2}
          y={REFERENCE_FACE_H_M + 1.05}
          textAnchor="middle"
          style={labelStyle}
        >
          Eave {eave} in
        </text>
        <text
          x={-0.55}
          y={REFERENCE_FACE_H_M / 2}
          textAnchor="middle"
          transform={`rotate(-90 ${-0.55} ${REFERENCE_FACE_H_M / 2})`}
          style={labelStyle}
        >
          Rake {rake} in
        </text>
      </svg>

      <p className="text-sm" role="status">
        <span className="font-medium tabular-nums">{preview.count}</span> panel positions —{' '}
        {deltaText}
      </p>

      {preview.count === 0 && (
        <p className="text-sm text-destructive">
          No panels fit inside these setbacks on this roof.
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        Reference only — a generic roof, never one of your customers&apos;. It shows what each
        setback does, not what fits a particular home.
      </p>
    </div>
  );
}
